import { isNil } from "lodash-es";
import { useState } from "react";

import AppIcon from "@/common/components/Icons/AppIcon";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { TenantRequestDto, TenantRequestStage, TenantRequestType } from "@/core/api/generated/v1";

import AppButton, { AppButtonProps } from "../../Button/AppButton";
import AppIconButton from "../../Button/AppIconButton";
import TenantRequestApplyResultModal from "./TenantRequestApplyResultModal";

interface Props {
  tenantRequest: TenantRequestDto;
  /** Either normal or icon buttons are rendered. */
  variant?: "normal" | "compact";
  isForOperation?: boolean;
  // Configuration to exclude loading = null to prevent errors in AppIconButton and AppButton
  buttonProps?: Omit<Partial<AppButtonProps>, "loading"> & { loading?: boolean };
  onEntityUpdate?: (newValue: TenantRequestDto) => void;
}

export default function TenantRequestApplyResultControl({
  tenantRequest,
  variant = "normal",
  isForOperation,
  buttonProps,
  onEntityUpdate,
}: Props) {
  const currentTenant = useCurrentTenant();
  const isSentByMe = currentTenant?.id === tenantRequest?.senderTenant?.id;
  // const isReceivedByMe = currentTenant?.id === tenantRequest?.receiverTenant?.id;
  const isVisible =
    isSentByMe &&
    tenantRequest?.stageHistory?.stage === TenantRequestStage.Closed &&
    (isNil(isForOperation) ||
      (isForOperation && tenantRequest.result?.type === TenantRequestType.Operation));
  const isApplied = tenantRequest?.appliedResult?.isApplied;

  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!isVisible) {
    return null;
  }

  return (
    <>
      {isVisible &&
        (variant === "normal" ? (
          <AppButton
            variant='contained'
            color='primary'
            size='small'
            fullWidth
            startIcon={<AppIcon of={!isApplied ? "apply" : "reapply"} />}
            {...buttonProps}
            onClick={() => setIsModalOpen(true)}
          >
            {!isApplied ? "Apply result" : "Reapply result"}
          </AppButton>
        ) : (
          <AppIconButton
            variant='outlined'
            color='primary'
            size='small'
            tooltipProps={{ title: !isApplied ? "Apply result" : "Reapply result" }}
            {...buttonProps}
            onClick={() => setIsModalOpen(true)}
          >
            <AppIcon of={!isApplied ? "apply" : "reapply"} />
          </AppIconButton>
        ))}

      <TenantRequestApplyResultModal
        applyProps={{
          tenantRequest: tenantRequest,
          defaultValues: {
            isReapply: isApplied,
          },
          onSave: (newValue) => {
            onEntityUpdate && onEntityUpdate(newValue);
            setIsModalOpen(false);
          },
        }}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}
